<template>
  <el-scrollbar class="side-scroll-bar" :style="{ height: scrollerHeight }">
    <div class="panel-container">
      <el-tabs v-model="firstTab" class="no-bottom-margin indent-left-margin">
        <el-tab-pane name="componentLib">
          <template #label>
            <span><svg-icon icon-class="el-set-up" /> {{ i18nt('designer.componentLib') }}</span>
          </template>

          <el-collapse v-model="activeNames" class="widget-collapse">
            <el-collapse-item name="1" :title="i18nt('designer.containerTitle')">
              <draggable
                tag="ul"
                :list="containers"
                item-key="key"
                :group="{ name: 'dragGroup', pull: 'clone', put: false }"
                :clone="handleContainerWidgetClone"
                ghost-class="ghost"
                :sort="false"
                :move="checkContainerMove"
                @end="onContainerDragEnd"
              >
                <template #item="{ element: ctn }">
                  <li class="container-widget-item" :title="ctn.displayName" @dblclick="addContainerByDbClick(ctn)">
                    <span><svg-icon :icon-class="ctn.icon" class-name="color-svg-icon" />{{ getWidgetLabel(ctn) }}</span>
                  </li>
                </template>
              </draggable>
            </el-collapse-item>

            <el-collapse-item name="2" :title="i18nt('designer.basicFieldTitle')">
              <draggable
                tag="ul"
                :list="basicFields"
                item-key="key"
                :group="{ name: 'dragGroup', pull: 'clone', put: false }"
                :move="checkFieldMove"
                :clone="handleFieldWidgetClone"
                ghost-class="ghost"
                :sort="false"
              >
                <template #item="{ element: fld }">
                  <li class="field-widget-item" :title="fld.displayName" @dblclick="addFieldByDbClick(fld)">
                    <span><svg-icon :icon-class="fld.icon" class-name="color-svg-icon" />{{ getWidgetLabel(fld) }}</span>
                  </li>
                </template>
              </draggable>
            </el-collapse-item>

            <el-collapse-item name="3" :title="i18nt('designer.advancedFieldTitle')">
              <draggable
                tag="ul"
                :list="advancedFields"
                item-key="key"
                :group="{ name: 'dragGroup', pull: 'clone', put: false }"
                :move="checkFieldMove"
                :clone="handleFieldWidgetClone"
                ghost-class="ghost"
                :sort="false"
              >
                <template #item="{ element: fld }">
                  <li class="field-widget-item" :title="fld.displayName" @dblclick="addFieldByDbClick(fld)">
                    <span><svg-icon :icon-class="fld.icon" class-name="color-svg-icon" />{{ getWidgetLabel(fld) }}</span>
                  </li>
                </template>
              </draggable>
            </el-collapse-item>

            <el-collapse-item name="4" :title="i18nt('designer.customFieldTitle')">
              <draggable
                tag="ul"
                :list="customFields"
                item-key="key"
                :group="{ name: 'dragGroup', pull: 'clone', put: false }"
                :move="checkFieldMove"
                :clone="handleFieldWidgetClone"
                ghost-class="ghost"
                :sort="false"
              >
                <template #item="{ element: fld }">
                  <li class="field-widget-item" :title="fld.displayName" @dblclick="addFieldByDbClick(fld)">
                    <span> <svg-icon :icon-class="fld.icon" class-name="color-svg-icon" />{{ getWidgetLabel(fld) }}</span>
                  </li>
                </template>
              </draggable>
            </el-collapse-item>
          </el-collapse>
        </el-tab-pane>

        <el-tab-pane v-if="showFormTemplates()" name="formLib" style="padding: 8px">
          <template #label>
            <span><svg-icon icon-class="el-form-template" /> {{ i18nt('designer.formLib') }}</span>
          </template>

          <template v-for="(ft, idx) in formTemplates" :key="idx">
            <el-card :bord-style="{ padding: '0' }" shadow="hover" class="ft-card">
              <el-popover placement="right" trigger="hover" popper-class="vform-widget-template">
                <template #reference>
                  <img :src="loadImageFromTemplate(ft.imgUrl)" style="width: 200px" />
                </template>
                <img :src="loadImageFromTemplate(ft.imgUrl)" style="width: 320px" />
              </el-popover>
              <div class="bottom clear-fix">
                <span class="ft-title">#{{ idx + 1 }} {{ ft.title }}</span>
                <el-button type="primary" link class="right-button" @click="loadFormTemplate(ft.jsonUrl)"> {{ i18nt('designer.hint.loadFormTemplate') }}</el-button>
              </div>
            </el-card>
          </template>
        </el-tab-pane>
      </el-tabs>
    </div>
  </el-scrollbar>
</template>

<script>
import { getCurrentInstance, reactive, nextTick, toRefs, inject, onMounted } from 'vue';

import SvgIcon from '@/components/svg-icon';
import { containers as CONS, basicFields as BFS, advancedFields as AFS, customFields as CFS } from './widgetsConfig';
import { formTemplates } from './templatesConfig';
import { addWindowResizeHandler, generateId } from '@/utils/util';
import { useI18n } from '@/utils/i18n';
import axios from 'axios';

export default {
  components: {
    SvgIcon,
  },
  name: 'FieldPanel',
  props: {
    designer: Object,
  },
  setup(props) {
    const { i18nt, i18n2t } = useI18n();
    const { proxy } = getCurrentInstance();
    let $current = proxy;

    const getBannedWidgets = inject('getBannedWidgets');
    const getDesignerConfig = inject('getDesignerConfig');

    /* 声明周期事件定义 */
    onMounted(() => {});

    const data = reactive({
      activeNames: ['1', '2', '3', '4'],

      advancedFields: [],

      basicFields: [],

      containers: [],

      customFields: [],
      designerConfig: getDesignerConfig(),
      firstTab: 'componentLib',
      formTemplates,

      scrollerHeight: 0,
    });

    /* 组件加载 */
    const loadWidgets = () => {
      data.containers = CONS.map(con => {
        return {
          key: generateId(),
          ...con,
          displayName: i18n2t(`designer.widgetLabel.${con.type}`, `extension.widgetLabel.${con.type}`),
        };
      }).filter(con => {
        return !con.internal && !isBanned(con.type);
      });

      data.basicFields = BFS.map(fld => {
        return {
          key: generateId(),
          ...fld,
          displayName: i18n2t(`designer.widgetLabel.${fld.type}`, `extension.widgetLabel.${fld.type}`),
        };
      }).filter(fld => {
        return !isBanned(fld.type);
      });

      data.advancedFields = AFS.map(fld => {
        return {
          key: generateId(),
          ...fld,
          displayName: i18n2t(`designer.widgetLabel.${fld.type}`, `extension.widgetLabel.${fld.type}`),
        };
      }).filter(fld => {
        return !isBanned(fld.type);
      });

      data.customFields = CFS.map(fld => {
        return {
          key: generateId(),
          ...fld,
          displayName: i18n2t(`designer.widgetLabel.${fld.type}`, `extension.widgetLabel.${fld.type}`),
        };
      }).filter(fld => {
        return !isBanned(fld.type);
      });

      if (import.meta.env.VITE_APP_ENV_STANDARD) {
        let deleteFields = ['m-sub-form', 'm-slot'];
        data.containers = data.containers.filter(x => {
          return (
            deleteFields.findIndex(f => {
              return f == x.type;
            }) == -1
          );
        });
        data.basicFields = data.basicFields.filter(x => {
          return (
            deleteFields.findIndex(f => {
              return f == x.type;
            }) == -1
          );
        });
        data.advancedFields = data.advancedFields.filter(x => {
          return (
            deleteFields.findIndex(f => {
              return f == x.type;
            }) == -1
          );
        });
      }
    };

    const isBanned = wName => {
      return getBannedWidgets().indexOf(wName) > -1;
    };

    const getWidgetLabel = widget => {
      if (widget.alias) {
        // 优先显示组件别名
        return i18n2t(`designer.widgetLabel.${widget.alias}`, `extension.widgetLabel.${widget.alias}`);
      }

      return i18n2t(`designer.widgetLabel.${widget.type}`, `extension.widgetLabel.${widget.type}`);
    };

    const showFormTemplates = () => {
      if (data.designerConfig['formTemplates'] === undefined) {
        return true;
      }

      return !!data.designerConfig['formTemplates'];
    };

    const handleContainerWidgetClone = origin => {
      return props.designer.copyNewContainerWidget(origin);
    };

    const handleFieldWidgetClone = origin => {
      return props.designer.copyNewFieldWidget(origin);
    };

    /* draggable组件的move钩子是在内部子组件被拖放到其他draggable组件时触发！！ */
    const checkContainerMove = evt => {
      return props.designer.checkWidgetMove(evt);
    };

    /* draggable组件的move钩子是在内部子组件被拖放到其他draggable组件时触发！！ */
    const checkFieldMove = evt => {
      return props.designer.checkFieldMove(evt);
    };

    const onContainerDragEnd = evt => {
      // console.log('Drag end of container: ')
      // console.log(evt)
    };

    const addContainerByDbClick = container => {
      props.designer.addContainerByDbClick(container);
    };

    const addFieldByDbClick = widget => {
      props.designer.addFieldByDbClick(widget);
    };
    const loadImageFromTemplate = imgUrl => {
      if (imgUrl.indexOf('http') === 0) {
        return imgUrl;
      } else {
        let imgName = imgUrl.substring(imgUrl.lastIndexOf('/') + 1);
        return require(`@/assets/ft-images/${imgName}`);
      }
    };
    const loadFormTemplate = jsonUrl => {
      $current
        .$confirm(i18nt('designer.hint.loadFormTemplateHint'), i18nt('render.hint.prompt'), {
          cancelButtonText: i18nt('render.hint.cancel'),
          confirmButtonText: i18nt('render.hint.confirm'),
        })
        .then(() => {
          axios
            .get(jsonUrl)
            .then(res => {
              let modifiedFlag = false;
              if (typeof res.data === 'string') {
                modifiedFlag = props.designer.loadFormJson(JSON.parse(res.data));
              } else if (res.data.constructor === Object) {
                modifiedFlag = props.designer.loadFormJson(res.data);
              }
              if (modifiedFlag) {
                props.designer.emitHistoryChange();
              }

              $current.$message.success(i18nt('designer.hint.loadFormTemplateSuccess'));
            })
            .catch(error => {
              $current.$message.error(i18nt('designer.hint.loadFormTemplateFailed') + ':' + error);
            });
        })
        .catch(error => {
          console.error(error);
        });
    };

    loadWidgets();
    data.scrollerHeight = window.innerHeight - 56 + 'px';
    addWindowResizeHandler(() => {
      data.scrollerHeight = window.innerHeight - 56 + 'px';
    });

    return {
      ...toRefs(data),

      addContainerByDbClick,

      addFieldByDbClick,
      checkContainerMove,
      checkFieldMove,
      getWidgetLabel,
      handleContainerWidgetClone,
      handleFieldWidgetClone,
      i18nt,
      loadFormTemplate,
      loadImageFromTemplate,
      onContainerDragEnd,
      showFormTemplates,
    };
  },
};
</script>

<style lang="scss" scoped>
.color-svg-icon {
  -webkit-font-smoothing: antialiased;
  color: #7c7d82;
}

.side-scroll-bar {
  :deep(.el-scrollbar__wrap) {
    overflow-x: hidden;
  }
}

div.panel-container {
  padding-bottom: 10px;
}

.no-bottom-margin :deep(.el-tabs__header) {
  margin-bottom: 0;
}

.indent-left-margin {
  :deep(.el-tabs__nav) {
    margin-left: 20px;
  }
}

.el-collapse-item :deep(ul) > li {
  list-style: none;
}

.widget-collapse {
  border-top-width: 0;

  :deep(.el-collapse-item__header) {
    margin-left: 8px;
    font-style: italic;
    font-weight: bold;
  }

  :deep(.el-collapse-item__content) {
    padding-bottom: 6px;

    ul {
      padding-left: 10px; /* 重置IE11默认样式 */
      margin: 0; /* 重置IE11默认样式 */
      margin-block-start: 0;
      margin-block-end: 0.25em;
      padding-inline-start: 10px;

      &:after {
        content: '';
        display: block;
        clear: both;
      }

      .container-widget-item,
      .field-widget-item {
        //text-align: center; // 居中显示不太美观
        display: inline-block;
        height: 32px;
        line-height: 32px;
        width: 98px;
        float: left;
        margin: 2px 6px 6px 0;
        cursor: move;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        background: #fff;
        border: 1px solid #dcdfe6;
        border-radius: 4px;
        padding: 0 8px;
      }

      .container-widget-item:hover,
      .field-widget-item:hover {
        background: #f1f2f3;
        border-color: $--color-primary;

        .color-svg-icon {
          color: $--color-primary;
        }
      }

      .drag-handler {
        position: absolute;
        top: 0;
        left: 160px;
        background-color: #dddddd;
        border-radius: 5px;
        padding-right: 5px;
        font-size: 11px;
        color: #666666;
      }
    }
  }
}

.el-card.ft-card {
  border: 1px solid #8896b3;
}

.ft-card {
  margin-bottom: 10px;

  .bottom {
    margin-top: 10px;
    line-height: 12px;
  }

  /*
    .image-zoom {
      height: 500px;
      width: 620px
    }
    */

  .ft-title {
    font-size: 13px;
    font-weight: bold;
  }

  .right-button {
    padding: 0;
    float: right;
  }

  .clear-fix:before,
  .clear-fix:after {
    display: table;
    content: '';
  }

  .clear-fix:after {
    clear: both;
  }
}
</style>
<style>
.el-popper.vform-widget-template {
  width: auto !important;
}
</style>
